import { useCallback } from "react";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import {
  doDisconnectGoogleCalendar,
  doGetGoogleCalendarStatus,
  doGetOAuthAuthorizeUrl,
  type U3201GoogleCalendarStatus,
} from "./U3201Service";
import {
  storeGoogleCalendarOAuthReturnOrgId,
  type U3201GoogleCalendarOAuthReturnLu,
} from "./U3201GoogleCalendarOAuthReturn";

export interface U3201GoogleCalendarHandlersParams {
  orgId: string | undefined;
  /** UL de retour après OAuth (défaut U2104). */
  oauthReturnLogicalUnit?: U3201GoogleCalendarOAuthReturnLu;
  onStatusSuccess?: (status: U3201GoogleCalendarStatus) => void;
  onStatusError?: (error: unknown) => void;
  onDisconnectSuccess?: () => void;
  onDisconnectError?: (error: unknown) => void;
  onOAuthError?: (error: unknown) => void;
}

export interface U3201GoogleCalendarHandlersReturn {
  fetchStatus: () => Promise<void>;
  disconnect: () => Promise<void>;
  startOAuth: () => Promise<void>;
}

export function useU3201GoogleCalendarHandlers({
  orgId,
  oauthReturnLogicalUnit = "U2104",
  onStatusSuccess,
  onStatusError,
  onDisconnectSuccess,
  onDisconnectError,
  onOAuthError,
}: U3201GoogleCalendarHandlersParams): U3201GoogleCalendarHandlersReturn {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const fetchStatus = useCallback(async () => {
    if (!orgId?.trim()) return;
    try {
      const response = await doGetGoogleCalendarStatus(u2000AxiosAuthenticated, orgId);
      onStatusSuccess?.(response.data.result);
    } catch (error) {
      onStatusError?.(error);
    }
  }, [orgId, u2000AxiosAuthenticated, onStatusSuccess, onStatusError]);

  const disconnect = useCallback(async () => {
    if (!orgId?.trim()) return;
    try {
      await doDisconnectGoogleCalendar(u2000AxiosAuthenticated, orgId);
      onDisconnectSuccess?.();
    } catch (error) {
      onDisconnectError?.(error);
    }
  }, [orgId, u2000AxiosAuthenticated, onDisconnectSuccess, onDisconnectError]);

  const startOAuth = useCallback(async () => {
    if (!orgId?.trim()) return;
    try {
      storeGoogleCalendarOAuthReturnOrgId(orgId, oauthReturnLogicalUnit);
      const response = await doGetOAuthAuthorizeUrl(u2000AxiosAuthenticated, orgId);
      window.location.assign(response.data.result.url);
    } catch (error) {
      onOAuthError?.(error);
    }
  }, [orgId, oauthReturnLogicalUnit, u2000AxiosAuthenticated, onOAuthError]);

  return { fetchStatus, disconnect, startOAuth };
}
